import { useState, useEffect } from 'react';
import { CheckCircleIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { format } from 'date-fns';
import { PaperAirplaneIcon } from '@heroicons/react/20/solid';
import ManagerNav from './managernav';
import { useNavigate } from 'react-router-dom';

export default function CurrentTask() {
  const [appraisals, setAppraisals] = useState([]);
  const [activeIndex, setActiveIndex] = useState(null);
  const [showAppraisalModal, setShowAppraisalModal] = useState(false);
  const [employeeComments, setEmployeeComments] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetch('https://api.insightos.co.uk/api/admintasks', {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    })
      .then(res => res.json())
      .then(data => setAppraisals(data.appraisal || []))
      .catch(console.error);
  }, []);

  const currentAppraisal = activeIndex !== null ? appraisals[activeIndex] : null;

  const handleSaveComments = async () => {
    if (!employeeComments.trim()) return;
    try {
      const response = await fetch('https://api.insightos.co.uk/api/saveemployeecomments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          employeeComments,
          appraisalId: currentAppraisal.id,
          author: currentAppraisal.Author,
          name: currentAppraisal.StaffName,
        }),
      });
      const data = await response.json();
      if (data.message?.includes('saved successfully')) navigate('/dashboard');
    } catch (error) {
      console.error('Failed to save comments:', error);
    }
  };

  return (
    <>
      <ManagerNav />
      <div className="p-4 bg-gradient-to-r from-indigo-500 to-blue-500 w-full rounded-xl flex flex-col sm:flex-row items-center justify-between shadow-lg mb-4">
        <p className="text-white text-xl font-bold mb-2 sm:mb-0">My Admin Tasks</p>
        <button
          onClick={() => setShowAppraisalModal(true)}
          className="flex items-center gap-2 rounded-full bg-white px-4 py-2 text-indigo-600 font-semibold shadow hover:bg-indigo-50 transition"
        >
          <CheckCircleIcon className="w-5 h-5" />
          Appraisal Outstanding <span className="font-bold">{appraisals.length}</span>
        </button>
      </div>

      {showAppraisalModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-2 p-6 relative overflow-y-auto max-h-[90vh]">
            <button
              className="absolute top-3 right-3 text-gray-400 hover:text-gray-700"
              onClick={() => setShowAppraisalModal(false)}
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
            <h2 className="text-lg font-bold text-indigo-700 mb-4 text-center">Outstanding Appraisals</h2>
            {appraisals.length > 0 ? (
              <>
                <label className="block text-sm font-medium mb-2">Select Appraisal:</label>
                <select
                  value={activeIndex !== null ? activeIndex : ""}
                  onChange={e => setActiveIndex(Number(e.target.value))}
                  className="w-full mb-4 rounded-lg border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
                >
                  <option value="" disabled>Select appraisal</option>
                  {appraisals.map((appraisal, idx) => (
                    <option key={idx} value={idx}>
                      {appraisal.ReviewPeriodFrom ? format(new Date(appraisal.ReviewPeriodFrom), 'MMM dd, yyyy') : 'N/A'}
                    </option>
                  ))}
                </select>
              </>
            ) : (
              <p className="text-gray-500 text-center">Nothing Outstanding</p>
            )}

            {currentAppraisal && (
              <div className="mt-2">
                <h3 className="text-base font-semibold text-indigo-600 mb-2">Appraisal Information</h3>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">Your Comments</label>
                  <div className="flex gap-2">
                    <textarea
                      value={employeeComments}
                      onChange={e => setEmployeeComments(e.target.value)}
                      rows={3}
                      className="flex-1 rounded-lg border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 p-2 text-sm"
                      placeholder="Enter your comments here"
                    />
                    <button
                      onClick={handleSaveComments}
                      data-component="current-task-comments"
                      data-time-saved="15"
                      data-feature="submit_employee_comments_post"
                      className="rounded-full bg-indigo-600 p-2 text-white shadow hover:bg-indigo-500 transition"
                    >
                      <PaperAirplaneIcon className="w-5 h-5" />
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  {[
                    ['Staff Name', currentAppraisal.StaffName],
                    ['Review Period From', currentAppraisal.ReviewPeriodFrom ? format(new Date(currentAppraisal.ReviewPeriodFrom), 'MMM dd, yyyy') : 'N/A'],
                    ['Review Period To', currentAppraisal.ReviewPeriodTo ? format(new Date(currentAppraisal.ReviewPeriodTo), 'MMM dd, yyyy') : 'N/A'],
                    ['Overall Performance', currentAppraisal.OverallPerformance],
                    ['Key Strengths', currentAppraisal.KeyStrengths],
                    ['Areas for Improvement', currentAppraisal.AreasForImprovement],
                    ['Performance Goals Met', currentAppraisal.PerformanceGoalsMet],
                    ['Examples Exceeding Expectations', currentAppraisal.ExamplesExceedingExpectations],
                    ['Deadlines Targets Met', currentAppraisal.DeadlinesTargetsMet],
                    ['Leadership and Teamwork Skills', currentAppraisal.LeadershipTeamworkSkills],
                    ['Feedback from Peers', currentAppraisal.FeedbackFromPeers],
                    ['Development Goals', currentAppraisal.DevelopmentGoals],
                    ['Recommended Training Courses', currentAppraisal.RecommendedTrainingCourses],
                    ['AI Training Report', currentAppraisal.AITrainingReport],
                    ['Skills for Career Advancement', currentAppraisal.SkillsForCareerAdvancement],
                    ['Compensation and Benefits Updates', currentAppraisal.CompensationBenefitsUpdates],
                    ['Overall Summary', currentAppraisal.OverallSummary],
                    ['Role Responsibility Changes', currentAppraisal.RoleResponsibilityChanges],
                    ['Action Plan', currentAppraisal.ActionPlan],
                    ['Employee Comments', currentAppraisal.EmployeeComments],
                    ['Author', currentAppraisal.Author],
                  ].map(([label, value], idx) => (
                    <div key={idx} className="flex flex-col sm:flex-row sm:items-center">
                      <span className="font-medium text-gray-700 w-48">{label}:</span>
                      <span className="text-gray-600">{value || 'N/A'}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}